import { FC, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getErrorFromCatch } from "../../api/api";
import { orderApi } from "../../api/order.api";
import { EMPTY_ORDER_SEARCH } from "../../api/orderFilter";
import { CONFIG } from "../../config";
import { IOrder } from "../../model/IOrder";
import { msg } from "../../msg";
import priceFormatter from "../../utils/priceFormatter";
import {
  feedPrice,
  firstItem,
  grossAmount,
  itemQty,
  partPrice,
  rawShipping,
  vendorPrice,
} from "../../utils/orderTotals";
import Loading from "../common/loading/Loading";
import LoadingOverlay from "../common/loading/LoadingOverlay";
import Modal from "../common/Modal";
import Truncate from "../common/Truncate";
import { sumOf } from "../../utils/tableTotals";
import { TDrilldownColumn } from "./categoryColumns";

const money = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : priceFormatter(value);

const day = (value: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-CA');
};

const stamp = (value: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : `${date.toLocaleDateString('en-CA')} ${date.toLocaleTimeString('en-GB')}`;
};

// --
// -- The footer's cell for one column.
// --
// -- Only the additive columns get one. Distance is per-order and Year is a
// -- model year: a column of numbers is not the same as a column that can be
// -- added up, and a total under either would mean nothing.
// --
const totalFor = (key: string, orders: IOrder[], label: string) => {
  switch (key) {
    case 'id': return label;
    case 'Qty': return sumOf(orders, (order) => itemQty(order)) ?? '';
    case 'Total': return money(sumOf(orders, (order) => order.totalPrice));
    case 'PartPrice': return money(sumOf(orders, (order) => partPrice(order)));
    case 'Shipping': return money(sumOf(orders, (order) => rawShipping(order)));
    case 'SalesTax': return money(sumOf(orders, (order) => order.taxes));
    case 'vPrice': return money(sumOf(orders, (order) => vendorPrice(order)));
    case 'FeedPrice': return money(sumOf(orders, (order) => feedPrice(order)));
    case 'Discount': return money(sumOf(orders, (order) => order.discountAmount));
    case 'Gross$': {
      const gross = sumOf(orders, (order) => grossAmount(order));
      return <span className={(gross ?? 0) < 0 ? 'text-pink-600' : ''}>{money(gross)}</span>;
    }
    default: return '';
  }
};

const cellFor = (key: string, order: IOrder) => {
  const item = firstItem(order);

  switch (key) {
    case 'id': return order.id ?? '—';
    case 'OrderID': return (
      <Link
        to={`/orders/${order.orderId}`}
        className="font-semibold text-primary hover:text-primary_hover underline underline-offset-2 whitespace-nowrap"
      >
        {order.orderId}
      </Link>
    );
    case 'Status': return <Truncate title={order.status || ''}>{order.status || '—'}</Truncate>;
    case 'Qty': return itemQty(order) ?? '—';
    case 'Total': return money(order.totalPrice);
    case 'PartPrice': return money(partPrice(order));
    case 'Shipping': return money(rawShipping(order));
    case 'SalesTax': return money(order.taxes);
    case 'vPrice': return money(vendorPrice(order));
    case 'FeedPrice': return money(feedPrice(order));
    case 'Discount': return money(order.discountAmount);
    case 'Gross$': return (
      <span className={(grossAmount(order) ?? 0) < 0 ? 'text-pink-600' : ''}>{money(grossAmount(order))}</span>
    );
    case 'website': return <Truncate title={order.fromSite || ''}>{order.fromSiteCleaned || order.fromSite || '—'}</Truncate>;
    case 'Name': return <Truncate title={order.siName || ''}>{order.siName || '—'}</Truncate>;
    case 'ShippedDate': return day(order.siDate);
    case 'CreatedDate': return stamp(order.timeCreate);
    case 'LastUpdated': return stamp(order.timeUpdate);
    case 'Distance': return order.distance ?? '—';
    case 'Coupon': return <Truncate title={order.couponCode || ''}>{order.couponCode || '—'}</Truncate>;
    case 'Year': return item?.viYear || '—';
    case 'PartType': return <Truncate title={item?.name || ''}>{item?.name || '—'}</Truncate>;
    case 'Stock#': return <Truncate title={item?.pid || ''}>{item?.pid || '—'}</Truncate>;
    default: return '—';
  }
};

// --
// -- The orders behind one row of a categorised report.
// --
// -- No endpoint of its own: this is the ordinary order search, narrowed by
// -- whatever the category means - a day, a vendor name, a vehicle. The
// -- per-order figures the reports want are sums over the items, and the
// -- search already ships every order with its items.
// --
const OrdersDrilldownModal: FC<{
  title: string,
  columns: TDrilldownColumn[],
  createdFrom?: string,
  createdTill?: string,
  vendorName?: string,
  vehicle?: string,
  partType?: string,
  status?: string,
  website?: string,
  state?: string,
  dma?: string,
  paymentType?: string,
  onClose: () => void,
}> = ({
  title, columns, createdFrom = '', createdTill = '',
  vendorName, vehicle, partType, status, website, state, dma, paymentType, onClose,
}) => {
  const navigate = useNavigate();
  const { data: orders, isFetching, isError, error } = orderApi.useGetOrderListQuery({
    page: 0,
    size: CONFIG.order.ordersPerDayDrilldown,
    q: '',
    sort: 'timeCreate',
    sortDir: 'desc',
    createdFrom,
    createdTill,
    ...EMPTY_ORDER_SEARCH,
    ...(vendorName ? { vendorName } : {}),
    ...(vehicle ? { vehicle } : {}),
    ...(partType ? { partType } : {}),
    ...(status ? { status } : {}),
    ...(website ? { website } : {}),
    ...(state ? { state } : {}),
    ...(dma ? { dma } : {}),
    ...(paymentType ? { paymentType } : {}),
  });

  useEffect(() => {
    isError && error && toast.error(`${msg('load-orders-filed')} ${getErrorFromCatch(error)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  const rows = orders?.content || [];
  const total = orders?.totalElements ?? 0;

  return (
    <Modal
      title={`${title}${total ? ` — ${total} order(s)` : ''}`}
      className="w-11/12 xl:w-11/12 2xl:w-11/12"
      setIsShown={onClose as never}
      onClose={onClose}
    >
      {/*
        Two different loading states. The first fetch has nothing to show, so
        the spinner stands in for the content; every fetch after that already
        has rows on screen, and those are dimmed under an overlay rather than
        thrown away - see LoadingOverlay.

        The wrapper is what the overlay is positioned against, and it sits
        outside the horizontal scroller on purpose: inside it, the overlay
        would slide off to the left as soon as anyone scrolled the table
        sideways.
      */}
      {isFetching && !orders
        ? <Loading />
        : (
          <div className="relative" aria-busy={isFetching}>
          {!rows.length
          ? <div className="text-text_pale py-4">No orders here</div>
          : (
            /*
              Bounded on purpose. overflow-x-auto makes this the scroll
              container in both axes, so without a height it never scrolls
              vertically and the `sticky top-0` headers below have nothing to
              stick to - they scroll away with the rows. With a cap the modal
              keeps its own table viewport and the headers stay put.
            */
            <div className="overflow-auto stylledScroll max-h-[60vh]">
              <table className="min-w-full border-separate border-spacing-0">
                <thead>
                  <tr>
                    {columns.map(({ key, title: heading }) => (
                      <th
                        key={key}
                        scope="col"
                        className="text-left font-bold whitespace-nowrap py-2 px-2 border-b-[1px] border-gray-300 sticky top-0 bg-white"
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((order) => (
                    <tr
                      key={order.id}
                      className="odd:bg-gray-50 hover:bg-gray-100 cursor-pointer"
                      title="Double-click to open this order"
                      onDoubleClick={() => navigate(`/orders/${order.orderId}`)}
                    >
                      {columns.map(({ key, right }) => (
                        <td key={key} className={`py-2 px-2 ${right ? 'text-right whitespace-nowrap' : ''}`}>
                          {cellFor(key, order)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                {/*
                  Capped at ordersPerDayDrilldown, so when there are more orders
                  than rows this totals what is shown rather than the category -
                  hence the label.
                */}
                <tfoot>
                  <tr className="bg-gray-100">
                    {columns.map(({ key, right }) => (
                      <td
                        key={key}
                        className={`py-2 px-2 font-semibold border-t-[2px] border-gray-300
                          ${right ? 'text-right whitespace-nowrap' : 'whitespace-nowrap'}`}
                      >
                        {totalFor(key, rows, total > rows.length ? `Total (${rows.length} shown)` : 'Total')}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>

              {total > rows.length && (
                <div className="text-text_pale pt-3">
                  Showing the first {rows.length} of {total}. Narrow the period to see the rest.
                </div>
              )}
            </div>
          )}

          {/* Only ever a refresh here: the first load took the branch above. */}
          {isFetching && <LoadingOverlay />}
          </div>
        )
      }
    </Modal>
  );
};

export default OrdersDrilldownModal;
